import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { RefreshCw } from "lucide-react";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ModuleAuditLogsTable } from "@/components/ModuleAuditLogsTable";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";

type ModuleGrantState = components["schemas"]["ModuleGrantState"];
type Module = components["schemas"]["ModuleResponse"];

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const stateOptions: { value: ModuleGrantState | "any"; label: string }[] = [
  { value: "any", label: "Any change" },
  { value: "Granted", label: "Grants only" },
  { value: "NotGranted", label: "Revocations only" },
];

function parseState(raw: string | null): ModuleGrantState | "any" {
  return stateOptions.some((o) => o.value === raw) ? (raw as ModuleGrantState | "any") : "any";
}

export function ModuleAuditLogsPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const [clientIdInput, setClientIdInput] = useState(searchParams.get("clientId") ?? "");
  const debouncedClientId = useDebouncedValue(clientIdInput.trim());

  const moduleKey = searchParams.get("moduleKey") ?? "any";
  const toState = parseState(searchParams.get("toState"));

  const [catalogue, setCatalogue] = useState<Module[]>([]);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    api.GET("/admin/modules").then(({ data }) => {
      if (!cancelled) setCatalogue(data ?? []);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  function updateParam(key: string, value: string | null) {
    setSearchParams(
      (params) => {
        if (value) {
          params.set(key, value);
        } else {
          params.delete(key);
        }
        return params;
      },
      { replace: true },
    );
  }

  // Only apply the client filter once it's a full UUID — the API expects uuid format.
  const clientId = UUID_PATTERN.test(debouncedClientId) ? debouncedClientId : undefined;

  const sortedCatalogue = [...catalogue].sort((a, b) => a.key.localeCompare(b.key));

  return (
    <div className="mx-auto w-full max-w-[100rem] p-8">
      <h1 className="mb-1 text-2xl font-bold">Modules Audit Log</h1>
      <p className="mb-6 text-sm text-muted-foreground">
        Every change to client entitlements: which module moved, whether it was granted or revoked,
        who changed it and why. Newest first.
      </p>

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Input
          placeholder="Filter by client ID…"
          value={clientIdInput}
          onChange={(e) => setClientIdInput(e.target.value)}
          aria-invalid={!!debouncedClientId && !clientId}
          spellCheck={false}
          autoComplete="off"
          className="w-80 font-mono text-xs"
        />
        <Select
          value={moduleKey}
          onValueChange={(v) => updateParam("moduleKey", v === "any" ? null : v)}
        >
          <SelectTrigger className="w-56">
            <SelectValue placeholder="Module" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="any">All modules</SelectItem>
            {sortedCatalogue.map((mod) => (
              <SelectItem key={mod.key} value={mod.key}>
                {mod.displayName} ({mod.key})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={toState}
          onValueChange={(v) => updateParam("toState", v === "any" ? null : v)}
        >
          <SelectTrigger className="w-48">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {stateOptions.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={() => setRefreshKey((k) => k + 1)}>
          <RefreshCw className="mr-2 size-4" />
          Refresh
        </Button>
      </div>

      <ModuleAuditLogsTable
        clientId={clientId}
        moduleKey={moduleKey === "any" ? undefined : moduleKey}
        toState={toState === "any" ? undefined : toState}
        refreshKey={refreshKey}
      />
    </div>
  );
}
